import { useState } from 'react'
import { useNow } from '@/hooks/useNow'
import { useStatus } from '@/hooks/useStatus'
import { relativeTime, serviceLabel } from '@/lib/format'

/**
 * The board losing its feed.
 *
 * The Worker and this app stay up when the home server does not, so an outage
 * is a condition to report, not an error to apologise for: an amber strip above
 * the masthead, stated once, in the same signage voice as everything else.
 *
 * The whole strip is the disclosure control rather than a small "Details"
 * link — that is the only way to clear a 44px touch target without making the
 * banner tall enough to push the masthead down the screen.
 */
export function OutageBanner() {
  const { isDegraded, down, services, checkedAt } = useStatus()
  const [expanded, setExpanded] = useState(false)
  const now = useNow(30_000)

  if (!isDegraded) return null

  const names = down.map((service) => serviceLabel(service.service))
  const summary =
    names.length === services.length
      ? 'The home server is offline'
      : `${names.slice(0, 2).join(' and ')}${names.length > 2 ? ` +${names.length - 2} more` : ''} ${
          names.length === 1 ? 'is' : 'are'
        } offline`

  return (
    <div role="status" aria-live="polite" className="border-b border-warn/25 bg-warn-dim">
      <button
        type="button"
        onClick={() => setExpanded((value) => !value)}
        aria-expanded={expanded}
        className="mx-auto flex min-h-11 w-full max-w-6xl items-center gap-x-2.5 gap-y-1 px-4 py-2 text-left"
      >
        <span aria-hidden="true" className="size-1.5 shrink-0 rounded-full bg-warn" />
        <span className="signage shrink-0 text-[9px] text-warn">No feed</span>
        <span className="min-w-0 flex-1 text-xs text-muted">
          <span className="text-ink">{summary}.</span> The portal still works; live progress is
          paused.
        </span>
        <span className="signage shrink-0 text-[9px] text-faint">
          {expanded ? 'Hide' : 'Details'}
        </span>
      </button>

      {expanded ? (
        <ul className="mx-auto max-w-6xl border-t border-warn/15 px-4 py-1 text-xs">
          {down.map((service) => (
            <li
              key={service.service}
              className="board-row flex flex-wrap items-baseline gap-x-2 py-1.5"
            >
              <span className="signage text-[9px] text-ink">{serviceLabel(service.service)}</span>
              <span className="font-mono text-[11px] text-muted">
                unreachable
                {service.checkedAt ? ` · ${relativeTime(service.checkedAt, now)}` : ''}
              </span>
              {service.message ? <span className="text-faint">{service.message}</span> : null}
            </li>
          ))}
          {checkedAt ? (
            <li className="board-row py-1.5 font-mono text-[11px] text-faint">
              Last health check {relativeTime(checkedAt, now)}
            </li>
          ) : null}
        </ul>
      ) : null}
    </div>
  )
}
